import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import LandingPage from "@/pages/marketing/LandingPage";
import PrivacyPage from "@/pages/marketing/PrivacyPage";
import TermsPage from "@/pages/marketing/TermsPage";
import RefundPage from "@/pages/marketing/RefundPage";
import CancellationPage from "@/pages/marketing/CancellationPage";
import SupportPage from "@/pages/marketing/SupportPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/auth/AuthPages";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import OnboardingPage from "@/pages/onboarding/OnboardingPage";

// Sprint 2 app pages (sidebar-based)
import DashboardPage from "@/pages/app/DashboardPage";
import AIAssistantPage from "@/pages/app/AIAssistantPage";
import ContentStudioPage from "@/pages/app/ContentStudioPage";
import ImagesPage from "@/pages/app/ImagesPage";
import VideosPage from "@/pages/app/VideosPage";
import VideoPilotPage from "@/pages/app/VideoPilotPage";
import ProQualityGatePage from "@/pages/app/ProQualityGatePage";
import KlingPilotPage from "@/pages/app/KlingPilotPage";
import SocialIntegrationsPage from "@/pages/app/SocialIntegrationsPage";
import ApprovalsPage from "@/pages/app/ApprovalsPage";
import BrandPage from "@/pages/app/BrandPage";
import SocialAccountsPage from "@/pages/app/SocialAccountsPage";
import AnalyticsPage from "@/pages/app/AnalyticsPage";
import CalendarPage from "@/pages/app/CalendarPage";
import ReportsPage from "@/pages/app/ReportsPage";
import TeamPage from "@/pages/app/TeamPage";
import BillingPage from "@/pages/app/BillingPage";
import SettingsPage from "@/pages/app/SettingsPage";
import ProfilePage from "@/pages/app/ProfilePage";
import ClientsPage from "@/pages/app/ClientsPage";
import AdminPanelPage from "@/pages/app/AdminPanelPage";
import PlansPage from "@/pages/app/PlansPage";
import LeadsPage from "@/pages/app/LeadsPage";
import LeadsAnalyticsPage from "@/pages/app/LeadsAnalyticsPage";
import CampaignEditorPage from "@/pages/app/CampaignEditorPage";
import PublicLeadPage from "@/pages/public/PublicLeadPage";
import PublicCampaignPage from "@/pages/public/PublicCampaignPage";
import { AdminDashboardPage } from "@/pages/dashboard/Dashboards";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  // Preserve the intended destination so LoginPage can send the user
  // back to (e.g.) /app/preview/video-pilot after a successful sign-in.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/cancellation" element={<CancellationPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/contact" element={<SupportPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* DAAM Leads — public page. No auth. */}
      <Route path="/l/:slug" element={<PublicLeadPage />} />
      <Route path="/l/:businessSlug/:campaignSlug" element={<PublicCampaignPage />} />

      <Route path="/onboarding" element={<Protected roles={["client"]}><OnboardingPage /></Protected>} />

      {/* Sprint 2 authenticated shell */}
      <Route path="/app/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/app/assistant" element={<Protected><AIAssistantPage /></Protected>} />
      <Route path="/app/studio" element={<Protected><ContentStudioPage /></Protected>} />
      <Route path="/app/images" element={<Protected><ImagesPage /></Protected>} />
      <Route path="/app/videos" element={<Protected><VideosPage /></Protected>} />
      <Route path="/app/preview/video-pilot"
             element={<Protected roles={["admin","owner","operator","super_admin"]}><VideoPilotPage /></Protected>} />
      <Route path="/app/preview/pro-gate"
             element={<Protected roles={["admin","owner","operator","super_admin"]}><ProQualityGatePage /></Protected>} />
      <Route path="/app/preview/kling-pilot"
             element={<Protected roles={["admin","owner","operator","super_admin"]}><KlingPilotPage /></Protected>} />
      <Route path="/app/preview/social"
             element={<Protected roles={["admin","owner","operator","super_admin"]}><SocialIntegrationsPage /></Protected>} />
      <Route path="/app/approvals"
             element={<Protected roles={["admin","owner","operator","super_admin"]}><ApprovalsPage /></Protected>} />
      <Route path="/app/brand" element={<Protected><BrandPage /></Protected>} />
      <Route path="/app/social" element={<Protected><SocialAccountsPage /></Protected>} />
      <Route path="/app/analytics" element={<Protected><AnalyticsPage /></Protected>} />
      <Route path="/app/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/app/reports" element={<Protected><ReportsPage /></Protected>} />
      <Route path="/app/team" element={<Protected><TeamPage /></Protected>} />
      <Route path="/app/plans" element={<Protected><PlansPage /></Protected>} />
      <Route path="/app/billing" element={<Protected><BillingPage /></Protected>} />
      <Route path="/app/clients" element={<Protected roles={["team", "admin", "super_admin"]}><ClientsPage /></Protected>} />
      <Route path="/app/admin" element={<Protected roles={["admin", "super_admin"]}><AdminPanelPage /></Protected>} />
      <Route path="/app/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/app/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/app/leads" element={<Protected roles={["client"]}><LeadsPage /></Protected>} />
      <Route path="/app/leads/analytics" element={<Protected roles={["client"]}><LeadsAnalyticsPage /></Protected>} />
      <Route path="/app/leads/campaigns/:id/edit" element={<Protected roles={["client"]}><CampaignEditorPage /></Protected>} />

      {/* Legacy Sprint 1 admin dashboard preserved at /app/admin/legacy */}
      <Route path="/app/admin/legacy" element={<Protected roles={["admin", "super_admin"]}><AdminDashboardPage /></Protected>} />

      {/* Legacy aliases */}
      <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/app/client" element={<Navigate to="/app/dashboard" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
